import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { assets } from '../constants/theme';

const navLinks = [
  { label: 'About', href: '/#about' },
  { label: 'Coffee Types', to: '/coffee-types' },
  { label: 'Our Process', to: '/process' },
  { label: 'Products', to: '/products' },
  { label: 'Our Vision', to: '/vision' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'unset';
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isActive = (to) => to && location.pathname === to;

  const s = {
    nav: {
      position: 'fixed',
      top: 0, left: 0,
      width: '100%',
      zIndex: 100,
      padding: scrolled ? '0.85rem 6%' : '1.5rem 6%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all 0.4s ease',
      backgroundColor: (scrolled || menuOpen || location.pathname !== '/')
        ? 'rgba(31,17,11,0.97)'
        : 'transparent',
      backdropFilter: (scrolled || menuOpen || location.pathname !== '/') ? 'blur(14px)' : 'none',
      boxShadow: scrolled ? '0 1px 30px rgba(0,0,0,0.4)' : 'none',
    },
    logoWrapper: {
      display: 'flex', alignItems: 'center', gap: '0.8rem',
      cursor: 'pointer', zIndex: 101, textDecoration: 'none',
    },
    logoImg: { height: '38px', width: 'auto', borderRadius: '50%' },
    logoText: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.55rem', fontWeight: 500, letterSpacing: '0.04em',
      color: '#F4EAE0',
    },
    logoAccent: { color: '#D96D29' },
    desktopLinks: { gap: '1.75rem', listStyle: 'none', alignItems: 'center' },
    link: (active) => ({
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 500,
      letterSpacing: '0.12em', textTransform: 'uppercase',
      color: active ? '#D96D29' : 'rgba(244,234,224,0.82)',
      transition: 'color 0.3s ease', cursor: 'pointer',
      textDecoration: 'none',
      borderBottom: active ? '1px solid #D96D29' : '1px solid transparent',
      paddingBottom: '2px',
    }),
    cta: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.73rem', fontWeight: 600,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      color: '#F4EAE0', backgroundColor: '#A63C1E',
      border: 'none', padding: '0.6rem 1.5rem',
      borderRadius: '2px', cursor: 'pointer',
      transition: 'background 0.3s ease, transform 0.2s ease',
      textDecoration: 'none',
    },
    hamburger: {
      background: 'none', border: 'none',
      color: '#F4EAE0', fontSize: '1.5rem',
      cursor: 'pointer', zIndex: 101,
    },
    mobileMenu: {
      position: 'fixed', top: 0, left: 0,
      width: '100%', height: '100vh',
      backgroundColor: '#1A0D07',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'center', alignItems: 'center',
      gap: '2rem', zIndex: 100,
      transition: 'opacity 0.4s ease, visibility 0.4s ease',
      opacity: menuOpen ? 1 : 0,
      visibility: menuOpen ? 'visible' : 'hidden',
    },
    mobileLink: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '2.2rem', color: '#F4EAE0', textDecoration: 'none',
    }
  };

  const renderLink = (l) => {
    if (l.to) {
      return (
        <Link key={l.label} to={l.to} style={s.link(isActive(l.to))}>
          {l.label}
        </Link>
      );
    }
    return (
      <a key={l.label} href={l.href} style={s.link(false)}>
        {l.label}
      </a>
    );
  };

  return (
    <nav style={s.nav} role="navigation" aria-label="Main navigation">
      <Link to="/" style={s.logoWrapper}>
        <img src={assets.logo} alt="Savana Sips Logo" style={s.logoImg}
          onError={(e) => e.target.style.display = 'none'} />
        <div style={s.logoText}>
          Savana <span style={s.logoAccent}>Sips</span>
        </div>
      </Link>

      <ul className="hide-on-mobile" style={s.desktopLinks}>
        {navLinks.map((l) => <li key={l.label}>{renderLink(l)}</li>)}
        <li>
          <Link to="/products" style={s.cta}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#D96D29'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A63C1E'}
          >
            Shop Now
          </Link>
        </li>
      </ul>

      <button className="show-on-mobile" style={s.hamburger}
        onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
        {menuOpen ? '✕' : '☰'}
      </button>

      <div style={s.mobileMenu}>
        {navLinks.map((l) => (
          l.to
            ? <Link key={l.label} to={l.to} style={s.mobileLink}>{l.label}</Link>
            : <a key={l.label} href={l.href} style={s.mobileLink}
                onClick={() => setMenuOpen(false)}>{l.label}</a>
        ))}
        <Link to="/products" style={{...s.cta, marginTop: '2rem', fontSize: '0.9rem', padding: '0.9rem 2.5rem'}}>
          Shop Now
        </Link>
      </div>
    </nav>
  );
}
